import { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Message } from "primereact/message";
import { apiService } from "../../api/api";
import { ChannelSettingsWithId } from "../../utils/@types";

const ChannelSettingsTab = () => {
  const [auditChannelIds, setAuditChannelIds] = useState<string[]>([]);
  const [allChannelSettings, setAllChannelSettings] = useState<
    ChannelSettingsWithId[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [addInput, setAddInput] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoadingId, setRemoveLoadingId] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ids, settings] = await Promise.all([
        apiService.oacAuditChannelIds.getChannelIds(),
        apiService.channelSettings.getAllChannelSettings(),
      ]);
      setAuditChannelIds(ids);
      setAllChannelSettings(Array.isArray(settings) ? settings : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const channelsInAudit = allChannelSettings.filter((ch) =>
    auditChannelIds.includes(ch.ChannelId || ch.id)
  );

  const handleAdd = async () => {
    const trimmed = addInput.trim();
    setAddError(null);
    if (!trimmed) {
      setAddError("Enter a channel ID");
      return;
    }
    setAddLoading(true);
    try {
      const validIds = await apiService.channelSettings.getChannelSettingsIds();
      if (!validIds || !validIds.includes(trimmed)) {
        setAddError("Channel ID not found in channel settings");
        setAddLoading(false);
        return;
      }
      await apiService.oacAuditChannelIds.addChannelId(trimmed);
      setAddInput("");
      await loadData();
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data
              ?.message
          : null;
      setAddError(msg || "Failed to add channel ID");
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemove = async (channelId: string) => {
    setRemoveLoadingId(channelId);
    try {
      await apiService.oacAuditChannelIds.removeChannelId(channelId);
      await loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setRemoveLoadingId(null);
    }
  };

  return (
    <div>
      <h3>Channel Settings</h3>
      <p className="mb-3">
        Manage which channel IDs are included in the OAC audit. Only channels
        that exist in channel settings can be added.
      </p>

      <div className="mb-4 flex align-items-center gap-2 flex-wrap">
        <InputText
          value={addInput}
          onChange={(e) => {
            setAddInput(e.target.value);
            setAddError(null);
          }}
          placeholder="Channel ID"
          className="p-inputtext-sm"
          style={{ minWidth: "16rem" }}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
        />
        <Button
          label="Add"
          icon="pi pi-plus"
          loading={addLoading}
          onClick={handleAdd}
          size="small"
        />
      </div>
      {addError && (
        <Message severity="error" text={addError} className="mb-3" />
      )}

      <h4 className="mt-4 mb-2">Channels in audit</h4>
      {loading ? (
        <p>Loading...</p>
      ) : channelsInAudit.length === 0 ? (
        <p>No channels in the audit list. Add channel IDs above.</p>
      ) : (
        <DataTable
          value={channelsInAudit}
          size="small"
          stripedRows
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25]}
        >
          <Column
            field="ChannelId"
            header="Channel ID"
            body={(row) => row.ChannelId || row.id}
          />
          <Column field="ChannelTitle" header="Channel Title" />
          <Column field="OwnerShortName" header="Owner" />
          <Column field="Type" header="Type" />
          <Column
            header=""
            body={(row) => {
              const id = row.ChannelId || row.id;
              return (
                <Button
                  icon="pi pi-trash"
                  severity="danger"
                  text
                  size="small"
                  loading={removeLoadingId === id}
                  onClick={() => handleRemove(id)}
                  aria-label={`Remove ${id}`}
                />
              );
            }}
          />
        </DataTable>
      )}
    </div>
  );
};

export default ChannelSettingsTab;
